import React from "react";
import styled, { css } from "styled-components";
import ToolTip from "../tooltip/ToolTip";

/**
 * テイク番号バッジ / 汎用の垂れ帯（タブ）ラベル。
 *
 * - size="default": メディア右上に貼り付く縦タブ（「テイク / n」の2段表示）
 * - size="small":   メッセージ（ファイル）右上に貼り付く極小タブ（数字のみ）
 * - size="inline":  表・リストのセル内に置ける静的配置のチップ（「テイクn」）
 *
 * `label` を渡すとテイク番号の代わりに任意テキストの垂れ帯になる
 * （例: "UPDATE"、やることリストの TIPS カードのカテゴリ名）。色は
 * `labelColor`（"blue" = アクション要求 / "gray" = 補足、既定 "gray"）。
 *
 * default / small は position: absolute のオーバーレイなので、親要素に
 * position: relative が必要。inline は通常のインラインフローに乗る。
 */

interface BaseLabelProps {
  $isHiding: boolean;
}

const baseLabelStyle = css<BaseLabelProps>`
  position: absolute;
  height: 42px;
  top: 0px;
  right: 12px;
  z-index: 6;
  color: #fff;
  padding: 8px 8px;
  border-radius: 0px 0px 6px 6px;
  opacity: ${(props) => (props.$isHiding ? 0 : 1)};
  transition: opacity 0.5s ease;
  pointer-events: ${(props) => (props.$isHiding ? "none" : "auto")};
`;

const TakeLabelStyle = styled.div.attrs({
  className: "u-col-center u-gap4",
})<BaseLabelProps & { $backgroundColor: string }>`
  ${baseLabelStyle}
  min-width: 16px;
  background-color: ${(props) => props.$backgroundColor};
`;

const TextLabelStyle = styled.div.attrs<
  BaseLabelProps & { $backgroundColor: string; $large: boolean }
>((props) => ({
  className: `u-col-center u-line-height-100 ${
    props.$large ? "bodytext-13" : "bodytext-11"
  }`,
}))<BaseLabelProps & { $backgroundColor: string; $large: boolean }>`
  ${baseLabelStyle}
  min-width: ${(props) => (props.$large ? "32px" : "20px")};
  max-width: 160px;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background-color: ${(props) => props.$backgroundColor};
`;

// 極小パターン: メッセージ（ファイル）アイテム右上に貼り付くタブ型バッジ（数字のみ）。
// 上端から下に少し垂れる見た目（下端のみ角丸）。背景色はテイク番号で変化する。
const SmallTakeLabelStyle = styled.div.attrs({
  className: "label8 u-line-height-100",
})<BaseLabelProps & { $backgroundColor: string }>`
  position: absolute;
  top: 0px;
  right: 8px;
  z-index: 6;
  color: #fff;
  padding: 4px 6px 6px;
  border-radius: 0px 0px 6px 6px;
  white-space: nowrap;
  background-color: ${(props) => props.$backgroundColor};
  opacity: ${(props) => (props.$isHiding ? 0 : 1)};
  transition: opacity 0.5s ease;
  pointer-events: ${(props) => (props.$isHiding ? "none" : "auto")};
`;

// インラインパターン: 表・リストのセル内に置ける静的チップ。
// 色はテイク番号で変化（default / small と共通のパレット）。
const InlineTakeLabelStyle = styled.span.attrs({
  className: "label8 u-line-height-100",
})<{ $backgroundColor: string }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: #fff;
  padding: 2px 6px 3px;
  border-radius: 4px;
  white-space: nowrap;
  background-color: ${(props) => props.$backgroundColor};
`;

export interface TakeLabelProps {
  /** テイク番号（1 始まり）。label 指定時は無視される */
  take?: number;
  /** 任意テキストの垂れ帯にする（例: "UPDATE"、カテゴリ名） */
  label?: string;
  /**
   * label 指定時の帯色。"blue" = ユーザーにアクションを求める、
   * "gray" = 補足表示（既定）。
   */
  labelColor?: "blue" | "gray";
  /**
   * label 指定時の帯サイズ。"large" = bodytext-13 / min-width 32px
   * （プロジェクトカード右上のバッジ合計など）。既定は bodytext-11 / 20px。
   */
  labelSize?: "default" | "large";
  /**
   * スクリーンリーダー向けの説明（label 指定時のみ有効）。
   * 数字だけの垂れ帯など、表示テキストだけでは意味が伝わらない場合に渡す。
   */
  ariaLabel?: string;
  /** フェードアウト表示（default / small のみ） */
  isHiding?: boolean;
  size?: "default" | "small" | "inline";
}

const OTHER_COLORS = ["#4ABDAC", "#F7B733", "#FC4A1A"];

const takeColor = (take: number): string =>
  take === 1
    ? "var(--soremo-blue)"
    : OTHER_COLORS[(take - 2) % OTHER_COLORS.length];

const TakeLabel: React.FC<TakeLabelProps> = ({
  take = 1,
  label,
  labelColor = "gray",
  labelSize = "default",
  ariaLabel,
  isHiding = false,
  size = "default",
}) => {
  if (label) {
    return (
      <ToolTip description={ariaLabel ?? label}>
        <TextLabelStyle
          $isHiding={isHiding}
          $large={labelSize === "large"}
          $backgroundColor={
            labelColor === "blue" ? "var(--soremo-blue)" : "var(--soremo-gray)"
          }
          aria-label={ariaLabel}
        >
          {label}
        </TextLabelStyle>
      </ToolTip>
    );
  }

  const backgroundColor = takeColor(take);

  // インラインパターン（表・リストのセル内バッジ）
  if (size === "inline") {
    return (
      <InlineTakeLabelStyle $backgroundColor={backgroundColor}>
        テイク{take}
      </InlineTakeLabelStyle>
    );
  }

  // 極小パターン（メッセージ右上のテイクバッジ）
  if (size === "small") {
    return (
      <SmallTakeLabelStyle
        $backgroundColor={backgroundColor}
        $isHiding={isHiding}
      >
        {take}
      </SmallTakeLabelStyle>
    );
  }

  return (
    <TakeLabelStyle $backgroundColor={backgroundColor} $isHiding={isHiding}>
      <div className="label8">テイク</div>
      <div className="bodytext u-line-height-100">{take}</div>
    </TakeLabelStyle>
  );
};

export default TakeLabel;
